/**
 * CognaraMacroLinkModal — one intelligent modal for auto-linking F&C
 * activities to Macro Schedule tasks.  No manual mode switch: Cognara
 * decides 1:1 vs 1:N by grouping.  After analysis the modal shows
 * "Macro → [F&C…]" groups so PMs can see exactly what will be linked.
 *
 * Grid layout:
 *   ┌───────────────────────────── Macro 1.1.1.1.4 · Rebar Work ─────┐
 *   │  [✓] CIV-004 · Rebar Installation — Area A       92%   [alt▾] │
 *   │  [✓] CIV-006 · Rebar Installation — Area B       88%   [alt▾] │
 *   │  [ ] CIV-011 · Rebar Installation — Area C       61%   (low)  │
 *   └───────────────────────────────────────────────────────────────┘
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Loader2, X, Sparkles, Check, RefreshCw, AlertTriangle, Search, Link2 } from 'lucide-react';

const CONFIDENCE_COLORS = {
  high:   { pill: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', bar: '#10b981' },
  medium: { pill: 'bg-amber-500/20   text-amber-300   border-amber-500/40',   bar: '#f59e0b' },
  low:    { pill: 'bg-red-500/20     text-red-300     border-red-500/40',     bar: '#ef4444' },
  already_linked: { pill: 'bg-slate-500/20 text-slate-300 border-slate-500/40', bar: '#64748b' },
};

const MODULE_BY_PREFIX = { '/eng': 'ENGINEERING', '/proc': 'PROCUREMENT', '/fc': 'FC', '/pccm': 'PCCM' };

export default function CognaraMacroLinkModal({ projectId, onClose, onCommitted, endpointPrefix = '/fc', moduleLabel = 'F&C', matchModes = null }) {
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [proposals, setProposals] = useState([]);
  const [summary, setSummary]   = useState(null);
  const [threshold, setThreshold] = useState(0.8);
  const [refineOn, setRefineOn] = useState(false);
  const [cascadeDates, setCascadeDates] = useState(true);
  const [filter, setFilter]     = useState('');
  const [matchMode, setMatchMode] = useState(matchModes ? matchModes[0].value : null);
  const [phase, setPhase]       = useState('');
  const [percent, setPercent]   = useState(0);
  const startedRef = useRef(false);
  const closedRef = useRef(false);
  useEffect(() => () => { closedRef.current = true; }, []);

  // per-fc override: fcActivityId -> chosen taskId (from alternatives)
  const [override, setOverride] = useState({});
  // per-fc explicit user selection: fcActivityId -> true (include) | false (exclude) | undefined (follow threshold)
  const [userChoice, setUserChoice] = useState({});

  // The run is a server-side job: it keeps going (and keeps reporting) even if
  // this modal is closed or the user walks to another page.
  const pollJob = useCallback(async (jobId) => {
    // eslint-disable-next-line no-constant-condition
    while (!closedRef.current) {
      const { data: js } = await api.get(`/link-jobs/${jobId}`);
      const job = js.job;
      setPhase(job.phase || '');
      setPercent(job.percent || 0);
      if (job.status === 'RUNNING') { await new Promise(r => setTimeout(r, 2000)); continue; }
      if (job.status !== 'COMPLETE') {
        toast.error(job.error || `Run ${job.status.toLowerCase()}`);
        return;
      }
      const { data: done } = await api.get(`/link-jobs/${jobId}?with_result=true`);
      setProposals(done.result?.proposals || []);
      setSummary(done.result?.summary || null);
      if (done.result?.summary?.matchMode) setMatchMode(done.result.summary.matchMode);
      setOverride({});
      setUserChoice({});
      toast.success(`Cognara analyzed ${done.result?.summary?.total || 0} activities`);
      return;
    }
  }, []);

  /** attachOnly = true never spends a Cognara call: it only shows an existing run. */
  const runPreview = useCallback(async (attachOnly = false) => {
    setLoading(true);
    setPhase(attachOnly ? 'Looking for an existing run…' : 'Starting the run…');
    setPercent(0);
    try {
      const { data: list } = await api.get('/link-jobs', { params: { project_id: projectId } });
      const mine = (list.jobs || []).filter(j => j.module === MODULE_BY_PREFIX[endpointPrefix]);
      const live = mine.find(j => j.status === 'RUNNING');
      if (live) {
        toast.info('A run is already in progress — showing its live progress');
        await pollJob(live.jobId);
        return;
      }
      if (attachOnly) {
        const recent = mine.find(j => j.status === 'COMPLETE');
        if (recent) { await pollJob(recent.jobId); }
        return;   // never start a paid run just because the modal opened
      }
      const { data } = await api.post(
        `/v1/projects/${projectId}${endpointPrefix}/cognara-macro-link/start`,
        { refine: refineOn, refine_top_n: 10, only_unlinked: false,
          ...(matchMode ? { match_mode: matchMode } : {}) },
      );
      await pollJob(data.jobId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); setPhase(''); }
  }, [projectId, refineOn, endpointPrefix, matchMode, pollJob]);

  useEffect(() => {
    if (startedRef.current) return;   // StrictMode double-mount must not pay twice
    startedRef.current = true;
    runPreview(true);
    /* eslint-disable-next-line */
  }, []);

  // ── Effective target task for each F&C (override or suggestion) ──
  const effectiveTarget = useCallback((p) => {
    const chosenId = override[p.fcActivityId];
    if (chosenId) {
      const alt = p.alternatives?.find(a => a.taskId === chosenId);
      if (alt) return { taskId: alt.taskId, wbsCode: alt.wbsCode, name: alt.name, confidence: alt.confidence };
    }
    return p.suggested
      ? { ...p.suggested, confidence: p.confidence }
      : null;
  }, [override]);

  // ── Auto-exclude when confidence < threshold (unless user explicitly kept it) ──
  const isBelowThreshold = (p) => {
    const tgt = effectiveTarget(p);
    if (!tgt) return true;
    return (tgt.confidence ?? 0) < threshold;
  };

  // ── Group by effective macro target ──
  const groups = useMemo(() => {
    const g = new Map();
    proposals.forEach(p => {
      const tgt = effectiveTarget(p);
      if (!tgt) {
        // Bucket the "no-match" rows separately
        const k = '__nomatch__';
        if (!g.has(k)) g.set(k, { taskId: null, wbsCode: '—', name: 'No match found', items: [] });
        g.get(k).items.push(p);
        return;
      }
      if (!g.has(tgt.taskId)) {
        g.set(tgt.taskId, { taskId: tgt.taskId, wbsCode: tgt.wbsCode, name: tgt.name, items: [] });
      }
      g.get(tgt.taskId).items.push(p);
    });
    // Filter
    const q = filter.trim().toLowerCase();
    const out = [];
    g.forEach((grp) => {
      const filtered = grp.items.filter(p => {
        if (!q) return true;
        return (p.fcActivityCode || '').toLowerCase().includes(q)
            || (p.fcActivityName || '').toLowerCase().includes(q)
            || (grp.wbsCode || '').toLowerCase().includes(q)
            || (grp.name || '').toLowerCase().includes(q);
      });
      if (filtered.length) out.push({ ...grp, items: filtered });
    });
    // Sort: no-match last, others by wbsCode
    out.sort((a, b) => {
      if (!a.taskId) return 1;
      if (!b.taskId) return -1;
      return (a.wbsCode || '').localeCompare(b.wbsCode || '');
    });
    return out;
  }, [proposals, effectiveTarget, filter]);

  // Is a proposal effectively included?  Explicit user choice always wins;
  // otherwise fall back to the threshold gate.
  const isIncluded = useCallback((p) => {
    if (p.status === 'already_linked') return false;
    const explicit = userChoice[p.fcActivityId];
    if (explicit === true)  return true;
    if (explicit === false) return false;
    const tgt = effectiveTarget(p);
    return !!tgt && (tgt.confidence ?? 0) >= threshold;
  }, [userChoice, threshold, effectiveTarget]);

  const acceptances = useMemo(() => {
    const out = [];
    proposals.forEach(p => {
      if (!isIncluded(p)) return;
      const tgt = effectiveTarget(p);
      if (!tgt || !tgt.taskId) return;
      out.push({
        fcActivityId: p.fcActivityId,
        taskId:       tgt.taskId,
        wbsCode:      tgt.wbsCode,
        name:         tgt.name,
        confidence:   tgt.confidence ?? p.confidence,
        rationale:    override[p.fcActivityId] ? `User override → ${tgt.name}` : p.rationale,
      });
    });
    return out;
  }, [proposals, override, effectiveTarget, isIncluded]);

  const distinctMacroCount = useMemo(
    () => new Set(acceptances.map(a => a.taskId)).size,
    [acceptances]
  );

  const commit = async () => {
    if (!acceptances.length) { toast.error('Nothing to commit'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}${endpointPrefix}/cognara-macro-link/commit`,
        { acceptances, replaceExisting: true, cascadeDates },
      );
      const msg = cascadeDates
        ? `Committed ${data.inserted + data.updated} links · ${data.datesCascaded || 0} dates cascaded`
        : `Committed ${data.inserted + data.updated} links`;
      toast.success(msg);
      onCommitted && onCommitted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
    } finally { setSaving(false); }
  };

  const toggleFC = (p) => {
    // Explicit toggle: currently-included → exclude · currently-excluded → include
    const current = isIncluded(p);
    setUserChoice(prev => ({ ...prev, [p.fcActivityId]: !current }));
  };

  const selectAll = () => {
    const next = {};
    proposals.forEach(p => {
      if (p.status !== 'already_linked' && effectiveTarget(p)) {
        next[p.fcActivityId] = true;
      }
    });
    setUserChoice(next);
  };

  const clearAll = () => {
    const next = {};
    proposals.forEach(p => { next[p.fcActivityId] = false; });
    setUserChoice(next);
  };

  const toggleGroup = (grp, includeAll) => {
    const next = { ...userChoice };
    grp.items.forEach(p => {
      if (p.status === 'already_linked') return;
      next[p.fcActivityId] = includeAll;
    });
    setUserChoice(next);
  };

  const overrideTarget = (fcActivityId, taskId) => {
    setOverride(prev => ({ ...prev, [fcActivityId]: taskId }));
    // Overriding a target implies "include"
    setUserChoice(prev => ({ ...prev, [fcActivityId]: true }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
         data-testid="cognara-link-modal">
      <div className="w-full max-w-6xl max-h-[90vh] rounded-xl border shadow-2xl flex flex-col"
           style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
            <div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                Cognara Auto-Link · {moduleLabel} Activity → Macro-Schedule Task
              </h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {loading
                  ? 'Analyzing…'
                  : summary
                    ? `${summary.total} ${moduleLabel} activities · ${summary.high} high · ${summary.medium} medium · ${summary.low} low${summary.cognaraRefined ? ` · ${summary.cognaraRefined} Cognara-refined` : ''} · Cognara auto-decided 1:1 vs 1:N by grouping ${moduleLabel} under each macro`
                    : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5"
                  style={{ color: 'var(--text-muted)' }} data-testid="cognara-link-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b text-xs"
             style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
          <div className="flex items-center gap-2">
            <label style={{ color: 'var(--text-muted)' }}>Auto-accept ≥</label>
            <input type="range" min="0.5" max="0.99" step="0.01"
                   value={threshold}
                   onChange={(e) => setThreshold(Number(e.target.value))}
                   className="w-32 accent-sky-500" data-testid="cognara-link-threshold" />
            <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{Math.round(threshold * 100)}%</span>
          </div>
          <label className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={refineOn} onChange={(e) => setRefineOn(e.target.checked)}
                   className="accent-sky-500" data-testid="cognara-link-refine-toggle" />
            Cognara refinement for ambiguous rows
          </label>
          <div className="relative ml-auto w-64">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)}
                   className="w-full pl-7 pr-2 py-1 rounded border text-xs"
                   style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                   data-testid="cognara-link-filter" />
          </div>
          <Button size="sm" variant="outline" onClick={selectAll}
                  data-testid="cognara-link-select-all">
            <Check className="w-3.5 h-3.5" /><span className="ml-1">Select all</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={clearAll}
                  data-testid="cognara-link-clear-all">Clear all</Button>
          <Button size="sm" variant="outline" onClick={() => runPreview(false)} disabled={loading}
                  data-testid="cognara-link-rerun">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1">{proposals.length ? 'Re-analyze' : 'Start analysis'}</span>
          </Button>
          {matchModes && (
            <select value={matchMode} onChange={e => setMatchMode(e.target.value)} disabled={loading}
                    className="px-2 py-1 rounded border text-xs" data-testid="cognara-link-match-mode"
                    style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              {matchModes.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-3 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}
                 data-testid="cognara-link-phase">
                {phase || `Cognara is matching ${moduleLabel} activities to macro-schedule tasks…`}
              </p>
              <div className="w-64 h-1.5 mt-3 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                <div className="h-full rounded-full transition-all" data-testid="cognara-link-progress-bar"
                     style={{ width: `${Math.max(3, Math.round(percent))}%`, background: 'var(--accent-primary)' }} />
              </div>
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                {Math.round(percent)}% · this run keeps going if you close this window
              </p>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-16 text-xs space-y-3" style={{ color: 'var(--text-muted)' }}>
              <p data-testid="cognara-link-empty">
                No analysis loaded yet. Pick your options above, then start the run —
                it keeps going in the background if you close this window.
              </p>
              <Button size="sm" onClick={() => runPreview(false)} data-testid="cognara-link-start">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Start Cognara analysis
              </Button>
            </div>
          ) : (
            groups.map((grp) => {
              const included = grp.items.filter(p => isIncluded(p)).length;
              const total = grp.items.filter(p => p.status !== 'already_linked').length;
              const allChecked = total > 0 && included === total;
              const isNomatch = !grp.taskId;
              return (
                <div key={grp.taskId || '__nomatch__'}
                     className="rounded-lg border overflow-hidden"
                     style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}
                     data-testid={`cognara-macro-group-${grp.wbsCode}`}>
                  {/* Group header — Macro Task */}
                  <div className="px-3 py-2 border-b flex items-center gap-2"
                       style={{ borderColor: 'var(--border-color)',
                                background: isNomatch ? 'rgba(239,68,68,0.08)' : 'rgba(56,189,248,0.06)' }}>
                    {!isNomatch && total > 0 && (
                      <input type="checkbox"
                             checked={allChecked}
                             onChange={() => toggleGroup(grp, !allChecked)}
                             className="accent-emerald-500"
                             data-testid={`cognara-group-toggle-${grp.wbsCode}`}
                             title={allChecked ? 'Uncheck all F&C in this macro group' : 'Check all F&C in this macro group'} />
                    )}
                    <Link2 className="w-3.5 h-3.5" style={{ color: isNomatch ? '#ef4444' : '#38bdf8' }} />
                    <span className="font-mono text-xs" style={{ color: isNomatch ? '#ef4444' : '#38bdf8' }}>
                      {grp.wbsCode}
                    </span>
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {grp.name}
                    </span>
                    <span className="ml-auto text-[10px] flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                      <span>{grp.items.length} {moduleLabel} matched · <b style={{ color: '#10b981' }}>{included}</b> will link</span>
                      {!isNomatch && grp.items.length > 1 && (
                        <span className="text-[9px] px-1 py-0.5 rounded"
                              style={{ background: 'rgba(139,92,246,0.16)', color: '#c084fc' }}>1 → N</span>
                      )}
                      {!isNomatch && grp.items.length === 1 && (
                        <span className="text-[9px] px-1 py-0.5 rounded"
                              style={{ background: 'rgba(148,163,184,0.16)', color: '#94a3b8' }}>1 : 1</span>
                      )}
                    </span>
                  </div>
                  {/* Group items — {moduleLabel} activities */}
                  <table className="w-full text-xs">
                    <tbody>
                      {grp.items.map((p) => {
                        const tgt = effectiveTarget(p);
                        const conf = tgt?.confidence ?? p.confidence ?? 0;
                        const stat = conf >= 0.85 ? 'high' : conf >= 0.7 ? 'medium' : 'low';
                        const statusStyle = p.status === 'already_linked'
                          ? CONFIDENCE_COLORS.already_linked
                          : CONFIDENCE_COLORS[stat] || CONFIDENCE_COLORS.medium;
                        const included = isIncluded(p);
                        return (
                          <tr key={p.fcActivityId}
                              className="border-t"
                              style={{ borderColor: 'var(--border-color)',
                                       background: included ? 'rgba(16,185,129,0.05)' : 'transparent' }}
                              data-testid={`cognara-link-row-${p.fcActivityCode}`}>
                            <td className="p-2 w-8">
                              {p.status === 'already_linked' ? (
                                <span className={`px-1.5 py-0.5 rounded text-[9px] border ${statusStyle.pill}`}>
                                  Linked
                                </span>
                              ) : (
                                <input type="checkbox"
                                       checked={included}
                                       onChange={() => toggleFC(p)}
                                       className="accent-emerald-500 cursor-pointer"
                                       data-testid={`cognara-link-toggle-${p.fcActivityCode}`} />
                              )}
                            </td>
                            <td className="p-2 whitespace-nowrap">
                              <div className="font-mono text-[11px] font-semibold" style={{ color: 'var(--accent-primary)' }}>
                                {p.fcActivityCode}
                              </div>
                              <div className="text-[11px]" style={{ color: 'var(--text-primary)' }}>{p.fcActivityName}</div>
                              <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                                {p.discipline} · {p.activityType}{p.area ? ` · ${p.area}` : ''}
                              </div>
                            </td>
                            <td className="p-2 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${statusStyle.pill}`}>
                                  {(conf * 100).toFixed(0)}%
                                </span>
                                {p.cognaraRefined && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/40"
                                        title="Refined by Cognara">Cognara</span>
                                )}
                              </div>
                              <div className="w-24 h-1 mt-1 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                                <div className="h-full rounded-full"
                                     style={{ width: `${conf * 100}%`, background: statusStyle.bar }} />
                              </div>
                            </td>
                            <td className="p-2 w-full">
                              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                {override[p.fcActivityId] ? '(User override) ' : ''}{p.rationale}
                              </div>
                              {p.alternatives?.length > 0 && (
                                <details className="mt-1">
                                  <summary className="text-[10px] cursor-pointer" style={{ color: 'var(--accent-primary)' }}>
                                    {p.alternatives.length} alternative{p.alternatives.length > 1 ? 's' : ''}
                                  </summary>
                                  <div className="mt-1 space-y-0.5 pl-3">
                                    {p.alternatives.map(alt => (
                                      <label key={alt.taskId}
                                             className="flex items-center gap-1 text-[10px] cursor-pointer hover:opacity-80"
                                             style={{ color: 'var(--text-muted)' }}>
                                        <input type="radio"
                                               name={`alt-${p.fcActivityId}`}
                                               checked={(override[p.fcActivityId] || p.suggested?.taskId) === alt.taskId}
                                               onChange={() => overrideTarget(p.fcActivityId, alt.taskId)}
                                               className="accent-sky-500"
                                               data-testid={`cognara-link-alt-${p.fcActivityCode}-${alt.taskId}`} />
                                        <span className="font-mono">{alt.wbsCode}</span>
                                        <span>{alt.name}</span>
                                        <span className="ml-auto">{alt.confidence ? `${(alt.confidence * 100).toFixed(0)}%` : ''}</span>
                                      </label>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <div>
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <AlertTriangle className="w-3.5 h-3.5" />
              {acceptances.length} {moduleLabel} activities → {distinctMacroCount} macro task{distinctMacroCount !== 1 ? 's' : ''} · {acceptances.length && distinctMacroCount < acceptances.length ? '(includes 1→N groups)' : '(all 1:1)'}
            </div>
            <label className="flex items-center gap-1.5 text-[11px] mt-1 cursor-pointer"
                   style={{ color: 'var(--text-muted)' }}
                   data-testid="cognara-link-cascade-toggle">
              <input type="checkbox"
                     checked={cascadeDates}
                     onChange={(e) => setCascadeDates(e.target.checked)}
                     className="accent-sky-500" />
              Cascade macro schedule dates (plan/forecast/actual) into linked {moduleLabel} activities and re-split scope
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="cognara-link-cancel">
              Cancel
            </Button>
            <Button size="sm" onClick={commit}
                    disabled={saving || !acceptances.length}
                    data-testid="cognara-link-commit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Commit {acceptances.length} link{acceptances.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
